import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import i18n from "../i18n";
import {
  Sparkles, Crown, Check, X as XIcon, Zap, Globe, Layers, FileDown, BarChart3,
  LineChart, Map, CalendarDays, NotebookPen, Users, Image as ImageIcon,
  ShieldCheck, HelpCircle, ArrowRight,
} from "lucide-react";

type Cycle = "monthly" | "yearly";

const PRICES = {
  monthly: 6.99,  // € / mois
  yearly: 59.99,   // € / an
};

// Mini hook i18n safe
function useI18nSafe() {
  const [, setLng] = useState((i18n.resolvedLanguage || i18n.language || "fr").toLowerCase());
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = (key: string, options?: Record<string, unknown>): string =>
    options != null ? String((i18n.t as any)(key, options)) : String((i18n.t as any)(key));
  return { t };
}

export const Pricing: React.FC = () => {
  const { t } = useI18nSafe();
  const [cycle, setCycle] = useState<Cycle>("monthly");

  const priceValue = cycle === "monthly" ? PRICES.monthly : PRICES.yearly;
  const yearlyVsMonthly = PRICES.monthly * 12;
  const saved = Math.max(0, yearlyVsMonthly - PRICES.yearly);
  const savedPct = yearlyVsMonthly > 0 ? Math.round((saved / yearlyVsMonthly) * 100) : 0;
  const monthlyEq = PRICES.yearly / 12;

  const sections = [
    {
      title: t("pricing:premiumGets.title"),
      items: [
        { icon: LineChart,    label: t("pricing:premiumGets.items.adv.title"),   desc: t("pricing:premiumGets.items.adv.desc") },
        { icon: ImageIcon,    label: t("pricing:premiumGets.items.res.title"),   desc: t("pricing:premiumGets.items.res.desc") },
        { icon: Layers,       label: t("pricing:premiumGets.items.teams.title"), desc: t("pricing:premiumGets.items.teams.desc") },
        { icon: NotebookPen,  label: t("pricing:premiumGets.items.plays.title"), desc: t("pricing:premiumGets.items.plays.desc") },
        { icon: Map,          label: t("pricing:premiumGets.items.heat.title"),  desc: t("pricing:premiumGets.items.heat.desc") },
      ],
    },
  ];

  const comparison = [
    t("pricing:compare.rows.0"),
    t("pricing:compare.rows.1"),
    t("pricing:compare.rows.2"),
    t("pricing:compare.rows.3"),
    t("pricing:compare.rows.4"),
    t("pricing:compare.rows.5"),
    t("pricing:compare.rows.6"),
    t("pricing:compare.rows.7"),
    t("pricing:compare.rows.8"),
  ].map((label, i) => ({
    label,
    free: i <= 3, // 0..3 = true, le reste false
    pro: true,
  }));

  return (
    <section className="relative min-h-screen w-full bg-gray-50 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
      {/* Fond décoratif */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute left-1/2 top-[-12rem] h-[28rem] w-[60rem] -translate-x-1/2 bg-gradient-to-r from-orange-500/20 via-fuchsia-500/20 to-cyan-500/20 blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(255,255,255,0.16),_transparent_60%)] dark:bg-[radial-gradient(ellipse_at_top,_rgba(255,255,255,0.06),_transparent_60%)]" />
      </div>

      <div className="mx-auto max-w-6xl px-4 py-16">
        {/* Hero */}
        <div className="mx-auto max-w-3xl rounded-2xl border border-white/40 bg-white/70 p-8 shadow-lg backdrop-blur-md dark:border-white/5 dark:bg-white/5">
          <div className="flex items-center justify-center gap-2">
            <Crown className="h-5 w-5 text-orange-600 dark:text-orange-300" />
            <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              {t("pricing:hero.badge")}
            </span>
          </div>
          <h1 className="mt-3 text-center font-[Rubik] text-3xl font-bold italic text-orange-600 dark:text-orange-400 sm:text-4xl">
            {t("pricing:hero.title")}
          </h1>
          <p className="mx-auto mt-3 max-w-2xl text-center text-gray-700 dark:text-gray-300">
            {t("pricing:hero.subtitle")}
          </p>

          {/* Toggle mensuel/annuel */}
          <div className="mt-6 flex items-center justify-center">
            <div role="tablist" aria-label={t("pricing:hero.toggleLabel")} className="inline-flex rounded-xl border border-gray-200 bg-white/80 p-1 text-sm shadow-sm dark:border-white/10 dark:bg-white/5">
              <button
                role="tab"
                aria-selected={cycle === "monthly"}
                className={`rounded-lg px-4 py-2 ${cycle === "monthly" ? "bg-orange-600 text-white" : "text-gray-700 dark:text-gray-300"}`}
                onClick={() => setCycle("monthly")}
              >
                {t("pricing:hero.monthly")}
              </button>
              <button
                role="tab"
                aria-selected={cycle === "yearly"}
                className={`rounded-lg px-4 py-2 ${cycle === "yearly" ? "bg-orange-600 text-white" : "text-gray-700 dark:text-gray-300"}`}
                onClick={() => setCycle("yearly")}
              >
                {t("pricing:hero.yearly", { save: savedPct })}
              </button>
            </div>
          </div>
        </div>

        {/* Cards plans */}
        <div className="mx-auto mt-10 grid max-w-5xl grid-cols-1 gap-4 sm:grid-cols-2">
          {/* Free */}
          <div className="relative rounded-2xl border border-gray-200 bg-white/80 p-6 shadow-sm dark:border-white/10 dark:bg-white/5">
            <div className="mb-2 text-sm font-medium text-gray-500 dark:text-gray-400">{t("pricing:free.badge")}</div>
            <h3 className="text-2xl font-semibold">Free</h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{t("pricing:free.lead")}</p>
            <div className="mt-4 flex items-baseline gap-2">
              <span className="text-3xl font-bold">0€</span>
              <span className="text-sm text-gray-500">{t("pricing:free.forever")}</span>
            </div>

            <ul className="mt-5 space-y-2 text-sm">
              {[
                { icon: BarChart3, text: t("pricing:free.features.0") },
                { icon: NotebookPen, text: t("pricing:free.features.1") },
                { icon: CalendarDays, text: t("pricing:free.features.2") },
                { icon: FileDown, text: t("pricing:free.features.3") },
              ].map((f, i) => (
                <li key={i} className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-600" />
                  <span className="flex items-center gap-2">
                    <f.icon className="h-4 w-4 opacity-60" />
                    {f.text}
                  </span>
                </li>
              ))}
            </ul>

            <div className="mt-6">
              <Link
                to="/signup"
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white/80 px-4 py-2.5 font-semibold text-gray-900 shadow-sm hover:bg-white dark:border-white/10 dark:bg-white/5 dark:text-gray-100"
              >
                {t("pricing:cta.create")}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          {/* Premium */}
          <div className="relative rounded-2xl border border-orange-300/50 bg-white/80 p-6 shadow-lg ring-1 ring-orange-500/10 dark:border-orange-300/20 dark:bg-white/5 dark:ring-0">
            <div className="absolute right-4 top-4 inline-flex items-center gap-1 rounded-full bg-orange-600 px-3 py-1 text-xs font-semibold text-white shadow-sm">
              <Sparkles className="h-3.5 w-3.5" />
              {t("pricing:pro.popular")}
            </div>
            <div className="mb-2 flex items-center gap-2">
              <Crown className="h-5 w-5 text-orange-600 dark:text-orange-300" />
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Premium</span>
            </div>
            <h3 className="text-2xl font-semibold">{t("pricing:pro.title")}</h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{t("pricing:pro.lead")}</p>
            <div className="mt-4 flex items-baseline gap-2">
              <span className="text-3xl font-bold">
                {priceValue.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€
              </span>
              <span className="text-sm text-gray-500">
                {cycle === "monthly" ? t("pricing:per.month") : t("pricing:per.year")}
              </span>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              {cycle === "monthly"
                ? t("pricing:hint.monthly")
                : t("pricing:hint.yearly", {
                    save: savedPct,
                    monthlyEq: monthlyEq.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                  })}
            </p>

            <ul className="mt-5 space-y-2 text-sm">
              {[
                { icon: LineChart,   text: t("pricing:pro.features.0") },
                { icon: ImageIcon,   text: t("pricing:pro.features.1") },
                { icon: Layers,      text: t("pricing:pro.features.2") },
                { icon: NotebookPen, text: t("pricing:pro.features.3") },
                { icon: Map,         text: t("pricing:pro.features.4") },
                { icon: Zap,         text: t("pricing:pro.features.5") },
                { icon: Globe,       text: t("pricing:pro.features.6") },
                { icon: Users,       text: t("pricing:pro.features.7") },
              ].map((f, i) => (
                <li key={i} className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-600" />
                  <span className="flex items-center gap-2">
                    <f.icon className="h-4 w-4 opacity-60" />
                    {f.text}
                  </span>
                </li>
              ))}
            </ul>

            <div className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Link
                to="/upgrade"
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-orange-600 px-4 py-2.5 font-semibold text-white shadow-sm ring-1 ring-orange-700/20 transition-transform hover:-translate-y-0.5 hover:bg-orange-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300"
              >
                {t("pricing:cta.upgrade")}
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white/70 px-4 py-2.5 font-semibold text-gray-900 shadow-sm hover:bg-white dark:border-white/10 dark:bg-white/5 dark:text-gray-100"
              >
                {t("pricing:cta.haveAccount")}
              </Link>
            </div>
          </div>
        </div>

        {/* Ce que tu obtiens */}
        <div className="mx-auto mt-12 max-w-5xl space-y-8">
          {sections.map((s, idx) => (
            <div key={idx} className="rounded-2xl border border-gray-200 bg-white/80 p-6 shadow-sm dark:border-white/10 dark:bg-white/5">
              <h4 className="text-lg font-semibold">{s.title}</h4>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {s.items.map((it, i) => (
                  <div key={i} className="rounded-xl border border-gray-100 bg-white/70 p-4 transition-colors hover:bg-white dark:border-white/10 dark:bg-white/5">
                    <div className="mb-2 inline-flex rounded-lg border border-orange-200/60 bg-orange-50/70 p-2 dark:border-orange-300/20 dark:bg-orange-300/10">
                      <it.icon className="h-5 w-5 text-orange-600 dark:text-orange-300" />
                    </div>
                    <div className="font-medium">{it.label}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">{it.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Comparatif */}
        <div className="mx-auto mt-12 max-w-5xl rounded-2xl border border-gray-200 bg-white/80 p-6 shadow-sm dark:border-white/10 dark:bg-white/5">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold">{t("pricing:compare.title")}</h3>
            <Crown className="h-5 w-5 text-orange-600 dark:text-orange-300" />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-gray-500 dark:border-white/10 dark:text-gray-400">
                  <th className="p-2">{t("pricing:compare.col.feature")}</th>
                  <th className="p-2">{t("pricing:compare.col.free")}</th>
                  <th className="p-2">{t("pricing:compare.col.pro")}</th>
                </tr>
              </thead>
              <tbody>
                {comparison.map((row, idx) => (
                  <tr key={idx} className="border-b border-gray-100 last:border-0 dark:border-white/5">
                    <td className="p-2">{row.label}</td>
                    <td className="p-2">{row.free ? <Check className="h-4 w-4 text-green-600" /> : <XIcon className="h-4 w-4 text-gray-400" />}</td>
                    <td className="p-2">{row.pro  ? <Check className="h-4 w-4 text-green-600" /> : <XIcon className="h-4 w-4 text-gray-400" />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* CTA secondaire */}
          <div className="mt-4 flex flex-col items-center justify-between gap-3 sm:flex-row">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t("pricing:compare.ctaLead")}
            </p>
            <div className="flex gap-2">
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 rounded-lg bg-orange-600 px-4 py-2 font-semibold text-white shadow-sm ring-1 ring-orange-700/20 transition-transform hover:-translate-y-0.5 hover:bg-orange-700"
              >
                {t("pricing:cta.create")}
              </Link>
              <Link
                to="/upgrade"
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white/70 px-4 py-2 font-semibold text-gray-900 shadow-sm hover:bg-white dark:border-white/10 dark:bg-white/5 dark:text-gray-100"
              >
                {t("pricing:cta.upgrade")}
              </Link>
            </div>
          </div>
        </div>

        {/* FAQ + technique */}
        <div className="mx-auto mt-12 grid max-w-5xl grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="rounded-xl border border-gray-200 bg-white/80 p-5 shadow-sm dark:border-white/10 dark:bg-white/5">
            <header className="mb-1 flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-orange-600 dark:text-orange-300" />
              <h4 className="font-semibold">{t("pricing:extras.security.title")}</h4>
            </header>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t("pricing:extras.security.desc")}
            </p>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white/80 p-5 shadow-sm dark:border-white/10 dark:bg-white/5">
            <header className="mb-2 flex items-center gap-2">
              <HelpCircle className="h-5 w-5 text-orange-600 dark:text-orange-300" />
              <h4 className="font-semibold">FAQ</h4>
            </header>

            <details className="group">
              <summary className="cursor-pointer select-none text-sm font-medium">{t("pricing:faq.q1.t")}</summary>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{t("pricing:faq.q1.b")}</p>
            </details>
            <hr className="my-2 border-gray-200 dark:border-white/10" />
            <details className="group">
              <summary className="cursor-pointer select-none text-sm font-medium">{t("pricing:faq.q2.t")}</summary>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{t("pricing:faq.q2.b")}</p>
            </details>
            <hr className="my-2 border-gray-200 dark:border-white/10" />
            <details className="group">
              <summary className="cursor-pointer select-none text-sm font-medium">{t("pricing:faq.q3.t")}</summary>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{t("pricing:faq.q3.b")}</p>
            </details>
            <hr className="my-2 border-gray-200 dark:border-white/10" />
            <details className="group">
              <summary className="cursor-pointer select-none text-sm font-medium">{t("pricing:faq.q4.t")}</summary>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{t("pricing:faq.q4.b")}</p>
            </details>
          </div>
        </div>

        {/* Footer callout */}
        <div className="mx-auto mt-12 max-w-3xl rounded-2xl border border-white/40 bg-white/70 p-6 text-center shadow-lg backdrop-blur-md dark:border-white/5 dark:bg-white/5">
          <p className="text-sm text-gray-700 dark:text-gray-300">{t("pricing:footer.callout")}</p>
          <div className="mt-3">
            <a
              href="https://discord.gg/q5zR75uTB6"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white/70 px-5 py-2.5 font-semibold text-gray-900 shadow-sm transition-transform hover:-translate-y-0.5 hover:bg-white dark:border-white/10 dark:bg-white/5 dark:text-gray-100"
            >
              {t("pricing:footer.discord")}
            </a>
          </div>
        </div>

        {/* Mentions prix */}
        <p className="mx-auto mt-4 max-w-3xl text-center text-xs text-gray-500 dark:text-gray-400">
          {t("pricing:legalNote")}
        </p>
      </div>
    </section>
  );
};
